import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { AuthenticationAdapter, PlatformAuthResponse } from '../adapters/authentication.adapter';
import { PlatformOnboardingPath } from '../enums/platform-onboarding-path.enum';
import {
  PLATFORM_EMPTY_USER,
  PlatformMe,
  PlatformUser,
} from '../models/platform.models';
import { PlatformAuthTokenService } from './platform-auth-token.service';
import { PlatformCandidateApiService } from './platform-candidate-api.service';

@Injectable({
  providedIn: 'root',
})
export class PlatformAuthService {
  private readonly tokens = inject(PlatformAuthTokenService);
  private readonly authAdapter = inject(AuthenticationAdapter);
  private readonly api = inject(PlatformCandidateApiService);

  private readonly meState = signal<PlatformMe | null>(null);
  private readonly onboardingPathState = signal<PlatformOnboardingPath | null>(null);
  private sessionRequest: Observable<boolean> | null = null;

  readonly session = this.meState.asReadonly();
  readonly loggedIn = computed(() => this.meState() !== null);
  readonly onboardingCompleted = computed(
    () => this.meState()?.onboardingCompleted === true
  );
  readonly hasPassword = computed(() => this.meState()?.hasPassword === true);
  readonly usedSso = computed(() => this.meState()?.usedSso === true);
  readonly onboardingPath = this.onboardingPathState.asReadonly();
  readonly user = computed((): PlatformUser | null => {
    const me = this.meState();
    if (!me) {
      return null;
    }
    return {
      firstName: me.firstName,
      lastName: me.lastName,
      email: me.email,
      hasCv: me.hasMainCv,
    };
  });

  constructor() {
    this.ensureSession().subscribe();
  }

  /**
   * Restores a candidate session from the stored JWT or the refresh cookie.
   * @returns Observable that emits whether a session is active
   */
  ensureSession(): Observable<boolean> {
    if (this.meState()) {
      return of(true);
    }
    if (!this.sessionRequest) {
      this.sessionRequest = this.api.me().pipe(
        tap((me) => this.applyMe(me)),
        map(() => true),
        catchError(() => {
          this.clearLocal();
          return of(false);
        }),
        tap(() => {
          this.sessionRequest = null;
        })
      );
    }
    return this.sessionRequest;
  }

  /**
   * Logs in with email and password and loads the candidate account.
   * @param email - Account email
   * @param password - Account password
   * @param rememberMe - Keep the session for 30 days
   * @param tfa - TOTP code when 2FA is enabled
   * @returns Observable of the loaded account
   */
  login(
    email: string,
    password: string,
    rememberMe: boolean,
    tfa?: string
  ): Observable<PlatformMe> {
    return this.authAdapter
      .login({ email, password, rememberMe, tfa })
      .pipe(switchMap((response) => this.adoptAuthResponse(response)));
  }

  /**
   * Registers a new candidate account (email-first).
   * @param payload - Registration payload
   * @returns Observable that completes when the mail is sent
   */
  register(payload: {
    email: string;
    captchaCode: string;
    termsAccepted: boolean;
    talenthubAccepted: boolean;
  }): Observable<void> {
    return this.authAdapter.register(payload);
  }

  /**
   * Signs in with a Google ID token and loads the candidate account.
   * @param payload - Google token and consents
   * @returns Observable of the loaded account
   */
  signInWithGoogle(payload: {
    token: string;
    rememberMe: boolean;
    termsAccepted?: boolean;
    talenthubAccepted?: boolean;
  }): Observable<PlatformMe> {
    return this.authAdapter
      .signInWithGoogle(payload)
      .pipe(switchMap((response) => this.adoptAuthResponse(response)));
  }

  /**
   * Sets a password from a mailed nonce.
   * @param nonce - Password-set nonce
   * @param password - New password
   * @returns Observable that completes when the password is stored
   */
  setPasswordWithNonce(nonce: string, password: string): Observable<void> {
    return this.authAdapter.setPassword({ nonce, password });
  }

  /**
   * Requests a password-reset mail.
   * @param email - Account email
   * @param captchaCode - FriendlyCaptcha token
   * @returns Observable that completes when the mail is sent
   */
  requestPasswordReset(email: string, captchaCode: string): Observable<void> {
    return this.authAdapter.passwordReset({ email, captchaCode });
  }

  /**
   * Requests a verification mail.
   * @param email - Account email
   * @param captchaCode - FriendlyCaptcha token
   * @returns Observable that completes when the mail is sent
   */
  sendEmailVerification(email: string, captchaCode: string): Observable<void> {
    return this.authAdapter.sendEmailVerification({ email, captchaCode });
  }

  /**
   * Verifies the candidate email with a nonce.
   * @param nonce - Verification nonce
   * @returns Observable that completes when verification succeeds
   */
  verifyEmail(nonce: string): Observable<void> {
    return this.authAdapter.emailVerification({ nonce });
  }

  /**
   * Changes or sets the password of the logged-in candidate.
   * @param newPassword - New password
   * @param currentPassword - Current password when one exists
   * @param tfa - TOTP code when 2FA is enabled
   * @returns Observable that completes when changed
   */
  changePassword(newPassword: string, currentPassword?: string, tfa?: string): Observable<void> {
    return this.api.changePassword(newPassword, currentPassword, tfa).pipe(
      tap(() => {
        const me = this.meState();
        if (me) {
          this.meState.set({ ...me, hasPassword: true });
        }
      })
    );
  }

  /**
   * Marks onboarding as done and refreshes the account.
   * @returns Observable of the updated account
   */
  completeOnboarding(): Observable<PlatformMe> {
    return this.api.completeOnboarding().pipe(tap((me) => this.applyMe(me)));
  }

  /**
   * Reloads the logged-in candidate account (lightweight /me).
   * @returns Observable of the updated account
   */
  refreshMe(): Observable<PlatformMe> {
    return this.api.me().pipe(tap((me) => this.applyMe(me)));
  }

  /**
   * Returns required login factors for an email.
   * @param email - Login email
   * @returns Observable with TOTP type list
   */
  getLoginAuthenticationMethods(email: string): Observable<{ types: string[] }> {
    return this.authAdapter.getAuthenticationMethods(email);
  }

  /**
   * Applies a freshly loaded me view to the session.
   * @param me - Account view
   */
  applyMe(me: PlatformMe): void {
    this.meState.set(me);
  }

  /**
   * Patches session name fields after a profile save.
   * @param firstName - First name
   * @param lastName - Last name
   * @param hasMainCv - Optional main-CV flag
   */
  patchSessionIdentity(
    firstName: string,
    lastName: string,
    hasMainCv?: boolean
  ): void {
    const me = this.meState();
    if (!me) {
      return;
    }
    this.meState.set({
      ...me,
      firstName,
      lastName,
      ...(hasMainCv !== undefined ? { hasMainCv } : {}),
    });
  }

  /**
   * Stores the chosen onboarding path for the current session.
   * @param path - Known or unknown applicant path
   */
  setOnboardingPath(path: PlatformOnboardingPath | null): void {
    this.onboardingPathState.set(path);
  }

  /**
   * Ends the candidate session.
   */
  logout(): void {
    this.authAdapter.logout().subscribe({ error: () => undefined });
    this.clearLocal();
  }

  /**
   * Deletes the platform account and ends the session.
   * @returns Observable that completes when deleted
   */
  deleteAccount(): Observable<void> {
    return this.api.deleteAccount().pipe(tap(() => this.clearLocal()));
  }

  /**
   * Fallback user for views that still expect a non-null display name.
   * @returns Current user or an empty placeholder
   */
  displayUser(): PlatformUser {
    return this.user() ?? PLATFORM_EMPTY_USER;
  }

  /**
   * Stores the JWT and loads the candidate account.
   * @param response - Auth API response
   * @returns Observable of the loaded account
   */
  private adoptAuthResponse(response: PlatformAuthResponse): Observable<PlatformMe> {
    this.tokens.set(response.jwt);
    return this.api.me().pipe(tap((me) => this.applyMe(me)));
  }

  /**
   * Clears the local JWT and session.
   */
  private clearLocal(): void {
    this.tokens.set(null);
    this.meState.set(null);
    this.onboardingPathState.set(null);
  }
}
